import { CompanyReportType } from '../CompanyReport';

/**
 * Investor report types that support a "Download formatted CSV" alternative.
 *
 * Each of these has a row-per-record TXT formatter; the CSV download produces
 * the same rows, but with named columns and raw values (no fixed-width
 * padding, hardcoded fillers, or zone-signed encoding).
 *
 * Out-of-scope:
 *   - GINNIE_RFS{,_EDIT} — already CSV.
 *   - GINNIE_LER: fixed-width GNMA submission file, no row-per-record CSV shape.
 *   - FHA_TS_264{,_EDIT} — X.12 EDI envelope, no row-per-record shape.
 *   - FANNIE_MAE_LAR_{32,81,83,89} and `_EDIT` variants — currently produce
 *     empty TXT, so there's nothing to render as CSV.
 */
export type CsvSupportedInvestorReportType =
  | typeof CompanyReportType.FANNIE_MAE_LAR_96_DAILY
  | typeof CompanyReportType.FANNIE_MAE_LAR_96_MONTHLY
  | typeof CompanyReportType.FANNIE_MAE_LAR_96_TRAILING
  | typeof CompanyReportType.FANNIE_MAE_LAR_96_EDIT
  | typeof CompanyReportType.FANNIE_MAE_CRS_DRAFT_REQUEST
  | typeof CompanyReportType.FANNIE_MAE_CRS_DRAFT_REQUEST_EDIT
  | typeof CompanyReportType.FANNIE_MAE_DELINQUENCY
  | typeof CompanyReportType.FANNIE_MAE_DELINQUENCY_EDIT
  | typeof CompanyReportType.FREDDIE_MAC_LLR_DAILY
  | typeof CompanyReportType.FREDDIE_MAC_LLR_MONTHLY
  | typeof CompanyReportType.FREDDIE_MAC_LLR_TRAILING
  | typeof CompanyReportType.FREDDIE_MAC_LLR_EDIT
  | typeof CompanyReportType.FREDDIE_MAC_EDR
  | typeof CompanyReportType.FREDDIE_MAC_EDR_EDIT;

const CSV_SUPPORTED_REPORT_TYPES: ReadonlySet<CompanyReportType> = new Set([
  CompanyReportType.FANNIE_MAE_LAR_96_DAILY,
  CompanyReportType.FANNIE_MAE_LAR_96_MONTHLY,
  CompanyReportType.FANNIE_MAE_LAR_96_TRAILING,
  CompanyReportType.FANNIE_MAE_LAR_96_EDIT,
  CompanyReportType.FANNIE_MAE_CRS_DRAFT_REQUEST,
  CompanyReportType.FANNIE_MAE_CRS_DRAFT_REQUEST_EDIT,
  CompanyReportType.FANNIE_MAE_DELINQUENCY,
  CompanyReportType.FANNIE_MAE_DELINQUENCY_EDIT,
  CompanyReportType.FREDDIE_MAC_LLR_DAILY,
  CompanyReportType.FREDDIE_MAC_LLR_MONTHLY,
  CompanyReportType.FREDDIE_MAC_LLR_TRAILING,
  CompanyReportType.FREDDIE_MAC_LLR_EDIT,
  CompanyReportType.FREDDIE_MAC_EDR,
  CompanyReportType.FREDDIE_MAC_EDR_EDIT,
]);

// Accepts a plain string so the GraphQL-generated `CompanyReportType` enum
// (a sibling, not the same nominal type) can be passed in directly from the
// frontend. The set is keyed by raw string values, which is also how both
// enums are encoded.
export const isCsvSupportedReportType = (type: string): type is CsvSupportedInvestorReportType =>
  CSV_SUPPORTED_REPORT_TYPES.has(type as CompanyReportType);
